import React, { useCallback, useEffect, useRef, useState } from 'react';
import Coachmark from './tutorial/Coachmark';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './Battle.css';
import './Arena.css';
import { authButtonSmallStyle, authButtonStyle, authCardStyle, authStatPanelStyle } from '../constants/authStyles';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { fetchActiveArenaMatchId } from '../arena/activeMatch';
import { useCharacterOptional } from '../context/CharacterContext';
import { intlLocaleFor } from '../i18n/languages';

// Sıralama tablosu sınırı: tüm listeyi değil, ilk sıraları + oyuncunun çevresini göster.
const RANK_LIMITS = {
  offline: { top: 5, above: 5, below: 2 }, // above=5: saldırılabilecek 5 oyuncu hep görünür
  online: { top: 5, above: 3, below: 3 },
  season: { top: 10, above: 2, below: 2 },
};

function limitRankingRows(rows, meId, limits) {
  const list = rows || [];
  const meIdx = list.findIndex((r) => r.userId === meId);
  const out = [];
  let prev = -1;
  list.forEach((row, i) => {
    const nearMe = meIdx >= 0 && i >= meIdx - limits.above && i <= meIdx + limits.below;
    if (i >= limits.top && !nearMe && !row.canAttack) return;
    if (prev >= 0 && i > prev + 1) out.push({ gap: true, key: `gap-${i}` });
    out.push(row);
    prev = i;
  });
  if (prev >= 0 && prev < list.length - 1) out.push({ gap: true, key: 'gap-end' });
  return out;
}

// Arama: sadece sayı yazılırsa o sıranın çevresi (±2), yazı yazılırsa ismi eşleşenler.
const RANK_SEARCH_AROUND = 2;
const RANK_SEARCH_MAX = 20;

function searchRankingRows(rows, query) {
  const list = rows || [];
  const q = (query || '').trim();
  const num = q.replace(/^#/, '');
  if (/^\d+$/.test(num)) {
    const pos = parseInt(num, 10);
    return list
      .filter((r) => r.position >= pos - RANK_SEARCH_AROUND && r.position <= pos + RANK_SEARCH_AROUND)
      .map((r) => (r.position === pos ? { ...r, searchHit: true } : r));
  }
  const needle = q.toLocaleLowerCase('tr');
  const hits = list.filter((r) => String(r.username || '').toLocaleLowerCase('tr').includes(needle));
  const out = hits.slice(0, RANK_SEARCH_MAX);
  if (hits.length > RANK_SEARCH_MAX) out.push({ gap: true, key: 'gap-search' });
  return out;
}


function formatSeasonLabel(year, month, language) {
  if (!year || !month) return '';
  const locale = intlLocaleFor(language);
  const raw = new Date(year, month - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  return raw.charAt(0).toLocaleUpperCase(locale) + raw.slice(1);
}

const Arena = () => {
  const { t, i18n } = useTranslation('arena');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const username = localStorage.getItem('username');
  const { setError, clearError } = usePopupMessage();
  const characterCtx = useCharacterOptional();

  const [tab, setTab] = useState(
    location.state?.arenaTab === 'online' || location.state?.arenaTab === 'seasons'
      ? location.state.arenaTab
      : 'offline'
  );
  const [offline, setOffline] = useState(null);
  const [online, setOnline] = useState(null);
  const [seasons, setSeasons] = useState(null);
  const [seasonKey, setSeasonKey] = useState('');
  const [onlineStatus, setOnlineStatus] = useState(null);
  const [savedFights, setSavedFights] = useState([]);
  const [fightPage, setFightPage] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);
  const [bonusBuying, setBonusBuying] = useState(false);
  const [bonusError, setBonusError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fightStarting, setFightStarting] = useState(false);
  const [searchSec, setSearchSec] = useState(0);
  const [rankQuery, setRankQuery] = useState({});
  const searchBaseRef = useRef({ sec: 0, at: 0 });

  const openOfflineFight = (fight) => {
    navigate('/battle', { state: { arenaOfflineFight: fight } });
  };

  const watchSavedFight = async (id, mode) => {
    try {
      const path = mode === 'online' ? '/api/arena/online/fight' : '/api/arena/offline/fight';
      const res = await axios.get(`${path}?username=${encodeURIComponent(username)}&id=${id}`);
      if (!res.data?.success || !res.data.fight) {
        setError(res.data?.error || t('errorFight'));
        return;
      }
      openOfflineFight(res.data.fight);
    } catch (e) {
      setError(e.response?.data?.error || t('errorFight'));
    }
  };

  const openOnlineFight = useCallback((matchId) => {
    if (!matchId) return;
    navigate('/battle', { state: { arenaOnlineMatch: { matchId } } });
  }, [navigate]);

  const loadOffline = useCallback(async () => {
    const res = await axios.get(`/api/arena/offline?username=${encodeURIComponent(username)}`);
    if (res.data.success) setOffline(res.data);
    else throw new Error(res.data.error || res.data.message);
  }, [username]);

  const loadOnline = useCallback(async () => {
    const res = await axios.get(`/api/arena/online?username=${encodeURIComponent(username)}`);
    if (res.data.success) setOnline(res.data);
    else throw new Error(res.data.error);
  }, [username]);

  const refresh = useCallback(async () => {
    try {
      clearError();
      setLoading(true);
      if (tab === 'seasons') {
        const params = new URLSearchParams({ username });
        if (seasonKey) {
          const [year, month] = seasonKey.split('-');
          if (year && month) {
            params.set('year', year);
            params.set('month', month);
          }
        }
        const res = await axios.get(`/api/arena/seasons?${params.toString()}`);
        if (!res.data?.success) throw new Error(res.data?.error || t('errorLoad'));
        setSeasons(res.data);
        const next = res.data.year && res.data.month ? `${res.data.year}-${res.data.month}` : '';
        if (next && next !== seasonKey) setSeasonKey(next);
      } else if (tab === 'offline') {
        await loadOffline();
        const saved = await axios.get(`/api/arena/offline/fights?username=${encodeURIComponent(username)}`);
        if (saved.data?.success) setSavedFights((saved.data.fights || []).filter((f) => f.kind !== 'friend'));
      } else {
        await loadOnline();
        const saved = await axios.get(`/api/arena/online/fights?username=${encodeURIComponent(username)}`);
        if (saved.data?.success) setSavedFights((saved.data.fights || []).filter((f) => f.kind !== 'friend'));
        const st = await axios.get(`/api/arena/online/status?username=${encodeURIComponent(username)}`);
        if (st.data.success) setOnlineStatus(st.data);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || t('errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [tab, seasonKey, username, loadOffline, loadOnline, clearError, setError, t]);

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    refresh();
  }, [username, navigate, tab, refresh]);

  useEffect(() => {
    if (!username) return undefined;
    let alive = true;
    (async () => {
      try {
        const matchId = await fetchActiveArenaMatchId(username);
        if (alive && matchId) openOnlineFight(matchId);
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, [username, openOnlineFight]);

  useEffect(() => {
    if (tab !== 'online' || onlineStatus?.status !== 'queued') return undefined;
    const id = setInterval(async () => {
      try {
        const st = await axios.get(`/api/arena/online/status?username=${encodeURIComponent(username)}`);
        if (st.data.success) {
          setOnlineStatus(st.data);
          if (st.data.status === 'matched' && st.data.matchId) {
            openOnlineFight(st.data.matchId);
          }
        }
      } catch (_) {}
    }, 2000);
    return () => clearInterval(id);
  }, [tab, onlineStatus?.status, username, openOnlineFight]);

  useEffect(() => {
    if (onlineStatus?.status !== 'queued') {
      setSearchSec(0);
      return undefined;
    }
    const id = setInterval(() => {
      const { sec, at } = searchBaseRef.current;
      setSearchSec(sec + Math.floor((Date.now() - at) / 1000));
    }, 200);
    return () => clearInterval(id);
  }, [onlineStatus?.status]);

  useEffect(() => {
    if (onlineStatus?.status !== 'queued') return;
    const server = Number(onlineStatus.waitSec);
    const sec = Number.isFinite(server) ? server : 0;
    searchBaseRef.current = { sec, at: Date.now() };
    setSearchSec(sec);
  }, [onlineStatus?.status, onlineStatus?.waitSec]);

  const attack = async (targetUserId) => {
    if (fightStarting) return;
    if ((offline?.dailyAttacks?.remaining ?? 0) <= 0) {
      setError(t('noDailyAttacks'));
      return;
    }
    setFightStarting(true);
    try {
      clearError();
      const res = await axios.post('/api/arena/offline/fight', { username, targetUserId });
      if (!res.data.success) {
        if (res.data.dailyAttacks) {
          setOffline((prev) => (prev ? { ...prev, dailyAttacks: res.data.dailyAttacks } : prev));
        }
        setError(res.data.error || t('errorFight'));
        setFightStarting(false);
        return;
      }
      openOfflineFight(res.data);
      await loadOffline();
    } catch (e) {
      const quota = e.response?.data?.dailyAttacks;
      if (quota) setOffline((prev) => (prev ? { ...prev, dailyAttacks: quota } : prev));
      setError(e.response?.data?.error || t('errorFight'));
      setFightStarting(false);
    }
  };

  const buyBonus = async () => {
    if (bonusBuying) return;
    setBonusBuying(true);
    setBonusError('');
    try {
      const res = await axios.post('/api/arena/offline/bonus', { username });
      if (!res.data.success) {
        setBonusError(res.data.error || t('errorLoad'));
        return;
      }
      setOffline((prev) => (prev ? { ...prev, dailyAttacks: res.data.dailyAttacks } : prev));
      if (typeof res.data.ruhParasi === 'number' && characterCtx?.setCharacter) {
        characterCtx.setCharacter((prev) => (prev ? { ...prev, ruhParasi: res.data.ruhParasi } : prev));
      }
      setBonusOpen(false);
    } catch (e) {
      setBonusError(e.response?.data?.error || t('errorLoad'));
    } finally {
      setBonusBuying(false);
    }
  };

  const queue = async () => {
    try {
      const res = await axios.post('/api/arena/online/queue', { username });
      if (!res.data.success) {
        setError(res.data.error || t('errorLoad'));
        return;
      }
      if (res.data.status === 'matched' && res.data.matchId) {
        openOnlineFight(res.data.matchId);
      }
      setOnlineStatus({ status: res.data.status, matchId: res.data.matchId });
    } catch (e) {
      setError(e.response?.data?.error || t('errorLoad'));
    }
  };

  const cancelQueue = async () => {
    await axios.post('/api/arena/online/cancel', { username });
    setOnlineStatus({ status: 'idle' });
  };

  const renderMe = (me, mode) => {
    if (!me || !me.userId) return null;
    const scoreLabel = mode === 'offline' ? t('points') : t('rating');
    const scoreValue = mode === 'offline' ? me.points : me.rating;
    return (
      <>
        <div className="arena-summary">
          <div className="arena-stat" style={authStatPanelStyle}>
            <span className="arena-stat-label">{t('position')}</span>
            <span className="arena-stat-value">#{me.position}</span>
          </div>
          <div className="arena-stat" style={authStatPanelStyle}>
            <span className="arena-stat-label">{t('rankTitle')}</span>
            <span className="arena-stat-value">{me.rankTitle}</span>
          </div>
          <div className="arena-stat" style={authStatPanelStyle}>
            <span className="arena-stat-label">{scoreLabel}</span>
            <span className="arena-stat-value">{scoreValue}</span>
          </div>
          <div className="arena-stat" style={authStatPanelStyle}>
            <button
              type="button"
              className="arena-stat-btn"
              onClick={() => setHelpOpen(true)}
              aria-label={t('helpLabel')}
            >
              {t('shortWins')} / {t('shortLosses')}
            </button>
            <span className="arena-stat-value">{me.wins} / {me.losses}</span>
          </div>
        </div>
      </>
    );
  };

  const renderColHead = (label, numeric = false) => (
    <button
      type="button"
      className={`arena-colhead${numeric ? ' arena-colhead--num' : ''}`}
      onClick={() => setHelpOpen(true)}
      aria-label={t('helpLabel')}
    >
      {label}
    </button>
  );

  const rankRowsFor = (boardKey, rows, meId, limits) => {
    const q = (rankQuery[boardKey] || '').trim();
    return q ? searchRankingRows(rows, q) : limitRankingRows(rows, meId, limits);
  };

  const renderRankSearch = (boardKey) => {
    const value = rankQuery[boardKey] || '';
    return (
      <div className="arena-rank-search">
        <input
          type="search"
          inputMode="search"
          className="arena-rank-search-input"
          placeholder={t('searchPlaceholder')}
          aria-label={t('searchPlaceholder')}
          value={value}
          onChange={(e) => setRankQuery((prev) => ({ ...prev, [boardKey]: e.target.value }))}
        />
        {value && (
          <button
            type="button"
            className="arena-rank-search-clear"
            aria-label={t('searchClear')}
            onClick={() => setRankQuery((prev) => ({ ...prev, [boardKey]: '' }))}
          >
            ×
          </button>
        )}
      </div>
    );
  };

  const renderTable = (rows, mode) => {
    const meId = mode === 'offline' ? offline?.me?.userId : online?.me?.userId;
    const shownRows = rankRowsFor(mode, rows, meId, RANK_LIMITS[mode]);
    return (
      <div className={`arena-board arena-board--${mode}`}>
        {renderRankSearch(mode)}
        <div className="arena-board-head">
          {renderColHead(t('shortRank'), true)}
          {renderColHead(t('shortPlayer'))}
          {renderColHead(mode === 'offline' ? t('shortPoints') : t('shortRating'), true)}
          {renderColHead(t('shortWins'), true)}
          {renderColHead(t('shortLosses'), true)}
          {mode === 'online' ? renderColHead(t('shortOnline'), true) : <span />}
        </div>
        <div className="arena-board-rows">
          {shownRows.length === 0 && <p className="arena-empty">{t('searchNoResults')}</p>}
          {shownRows.map((row) => (row.gap ? (
            <div key={row.key} className="arena-board-gap" aria-hidden="true">⋯</div>
          ) : (
            <div
              key={row.userId}
              className={`arena-board-row${row.userId === meId ? ' is-me' : ''}${row.searchHit ? ' is-hit' : ''}`}
            >
              <div className="arena-rank">{row.position}</div>
              <div className="arena-player">
                <div className="arena-player-name">{row.username}</div>
                <div className="arena-player-title">{row.rankTitle}</div>
              </div>
              <div className="arena-num">{mode === 'offline' ? row.points : row.rating}</div>
              <div className="arena-num">{row.wins}</div>
              <div className="arena-num">{row.losses}</div>
              {mode === 'online' ? (
                <span
                  className={`arena-online-dot${row.online ? ' arena-online-dot--on' : ''}`}
                  title={row.online ? t('online') : t('offline')}
                />
              ) : (
                <div>
                  {row.canAttack && (
                    <button
                      type="button"
                      className="auth-button auth-button--compact arena-row-btn"
                      style={authButtonSmallStyle}
                      onClick={() => attack(row.userId)}
                      disabled={fightStarting || (offline?.dailyAttacks?.remaining ?? 0) <= 0}
                    >
                      {t('attack')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )))}
        </div>
      </div>
    );
  };

  const renderSeasonBoard = (rows, mode) => {
    const mine = mode === 'offline' ? seasons?.offline?.me?.userId : seasons?.online?.me?.userId;
    const boardKey = `season-${mode}`;
    const shownRows = rankRowsFor(boardKey, rows, mine, RANK_LIMITS.season);
    return (
      <div className="arena-board arena-board--season">
        {renderRankSearch(boardKey)}
        <div className="arena-board-head">
          {renderColHead(t('shortRank'), true)}
          {renderColHead(t('shortPlayer'))}
          {renderColHead(mode === 'offline' ? t('shortPoints') : t('shortRating'), true)}
          {renderColHead(t('shortWins'), true)}
          {renderColHead(t('shortLosses'), true)}
        </div>
        <div className="arena-board-rows">
          {shownRows.length === 0 && <p className="arena-empty">{t('searchNoResults')}</p>}
          {shownRows.map((row) => (row.gap ? (
            <div key={`${mode}-${row.key}`} className="arena-board-gap" aria-hidden="true">⋯</div>
          ) : (
            <div
              key={`${mode}-${row.userId}`}
              className={`arena-board-row${row.userId === mine ? ' is-me' : ''}${row.searchHit ? ' is-hit' : ''}`}
            >
              <div className="arena-rank">{row.position}</div>
              <div className="arena-player">
                <div className="arena-player-name">{row.username}</div>
                <div className="arena-player-title">{row.rankTitle}</div>
              </div>
              <div className="arena-num">{mode === 'offline' ? row.points : row.rating}</div>
              <div className="arena-num">{row.wins}</div>
              <div className="arena-num">{row.losses}</div>
            </div>
          )))}
        </div>
      </div>
    );
  };

  const placeText = (me) => (me?.position ? `#${me.position}` : '—');

  const renderFights = (mode) => {
    if (savedFights.length === 0) {
      return <p className="arena-empty">{t('noReplays')}</p>;
    }
    const pageSize = 5;
    const pageCount = Math.max(1, Math.ceil(savedFights.length / pageSize));
    const page = Math.min(fightPage, pageCount - 1);
    const start = page * pageSize;
    const pageFights = savedFights.slice(start, start + pageSize);
    return (
      <div className="arena-board arena-board--fights">
        <div className="arena-board-head">
          {renderColHead(t('shortPlayer'))}
          {renderColHead(`${t('shortWins')}/${t('shortLosses')}`, true)}
          {renderColHead(mode === 'offline' ? t('shortPoints') : t('shortRating'), true)}
          <span />
        </div>
        <div className="arena-board-rows">
          {pageFights.map((fight, index) => {
            const delta = mode === 'offline' ? fight.pointsDelta : fight.ratingDelta;
            const canWatch = start + index < pageSize;
            return (
              <div key={fight.id} className="arena-board-row">
                <div className="arena-fight-name">{fight.opponent}</div>
                <button
                  type="button"
                  className="arena-colhead arena-colhead--num"
                  onClick={() => setHelpOpen(true)}
                  aria-label={t('helpLabel')}
                >
                  {fight.draw ? t('shortDraw') : fight.won ? t('shortWins') : t('shortLosses')}
                </button>
                <div className={`arena-delta${delta > 0 ? ' arena-delta--up' : delta < 0 ? ' arena-delta--down' : ''}`}>
                  {delta > 0 ? `+${delta}` : delta}
                </div>
                {canWatch ? (
                  <button
                    type="button"
                    className="auth-button auth-button--compact arena-row-btn"
                    style={authButtonSmallStyle}
                    onClick={() => watchSavedFight(fight.id, mode)}
                  >
                    {t('watchReplay')}
                  </button>
                ) : <span />}
              </div>
            );
          })}
        </div>
        {pageCount > 1 && (
          <div className="arena-pager">
            <button
              type="button"
              className="auth-button auth-button--compact"
              style={authButtonSmallStyle}
              disabled={page <= 0}
              onClick={() => setFightPage(page - 1)}
            >
              {t('pagePrev')}
            </button>
            <span className="arena-pager-label">{page + 1} / {pageCount}</span>
            <button
              type="button"
              className="auth-button auth-button--compact"
              style={authButtonSmallStyle}
              disabled={page >= pageCount - 1}
              onClick={() => setFightPage(page + 1)}
            >
              {t('pageNext')}
            </button>
          </div>
        )}
      </div>
    );
  };

  const searching = onlineStatus?.status === 'queued';
  const myRating = Number(online?.me?.rating) || 1000;
  const searchPhase = searchSec >= 30 ? 'any' : searchSec >= 15 ? 'wide' : 'close';
  const searchGap = searchPhase === 'wide' ? 400 : 200;
  const searchMin = Math.max(100, myRating - searchGap);
  const searchMax = myRating + searchGap;

  return (
    <div className="auth-container auth-container--selection arena-page">
      <Coachmark
        tourId="arena"
        steps={[
          { selector: '.arena-summary', textKey: 'arenaSummary' },
          { selector: '', textKey: 'arenaEfsun' },
      ]}
      />
      <AuthLayeredCard>
        <AuthSelectionToolbar
          onBack={() => { if (!fightStarting) navigate('/game'); }}
          header={(
            <h1 className="arena-title-with-help">
              {t('title')}
              <button
                type="button"
                className="arena-help-btn"
                aria-label={t('helpLabel')}
                onClick={() => setHelpOpen(true)}
              >
                ?
              </button>
            </h1>
          )}
        />
        <div className="battle-nav-row">
          <button
            type="button"
            className={`auth-button auth-button--compact battle-nav-btn${tab === 'offline' ? ' battle-subtab--active' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => { if (fightStarting) return; setFightPage(0); setTab('offline'); }}
            disabled={fightStarting}
          >
            {t('tabOffline')}
          </button>
          <button
            type="button"
            className={`auth-button auth-button--compact battle-nav-btn${tab === 'online' ? ' battle-subtab--active' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => { if (fightStarting) return; setFightPage(0); setTab('online'); }}
            disabled={fightStarting}
          >
            {t('tabOnline')}
          </button>
          <button
            type="button"
            className={`auth-button auth-button--compact battle-nav-btn${tab === 'seasons' ? ' battle-subtab--active' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => { if (fightStarting) return; setTab('seasons'); }}
            disabled={fightStarting}
          >
            {t('tabSeasons')}
          </button>
        </div>
        {loading && tab !== 'seasons' && !offline && !online && <p className="arena-empty">{tc('loading')}</p>}
        {loading && tab === 'seasons' && !seasons && <p className="arena-empty">{tc('loading')}</p>}
        {tab === 'offline' && offline && (
          <>
            {renderMe(offline.me, 'offline')}
            <h2 className="arena-section-title arena-section-title--quota">
              {t('leaderboard')}
              {offline.dailyAttacks && <span>({offline.dailyAttacks.remaining})</span>}
              {offline.dailyAttacks && !offline.dailyAttacks.bonus && (
                <button
                  type="button"
                  className="arena-daily-plus"
                  aria-label={t('bonusTitle')}
                  onClick={() => { setBonusError(''); setBonusOpen(true); }}
                >
                  +
                </button>
              )}
            </h2>
            {renderTable(offline.ranking, 'offline')}
            {(!offline.ahead || offline.ahead.length === 0) && offline.me?.position <= 1 && (
              <p className="arena-empty">{t('noTargets')}</p>
            )}
            <h2 className="arena-section-title">{t('recentFights')}</h2>
            {renderFights('offline')}
          </>
        )}
        {tab === 'online' && online && (
          <>
            {renderMe(online.me, 'online')}
            <div className="arena-actions">
              {onlineStatus?.status === 'queued' ? (
                <>
                  <span className="arena-waiting">{t('waiting')}</span>
                  <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={cancelQueue}>{t('cancel')}</button>
                </>
              ) : onlineStatus?.status === 'matched' ? (
                <button type="button" className="auth-button auth-button--compact" style={authButtonStyle} onClick={() => openOnlineFight(onlineStatus.matchId)}>
                  {t('matched')}
                </button>
              ) : (
                <button type="button" className="auth-button auth-button--compact" style={authButtonStyle} onClick={queue}>{t('match')}</button>
              )}
            </div>
            <h2 className="arena-section-title">{t('leaderboard')}</h2>
            {renderTable(online.ranking, 'online')}
            <h2 className="arena-section-title">{t('recentFights')}</h2>
            {renderFights('online')}
          </>
        )}
        {tab === 'seasons' && seasons && (
          seasons.seasons?.length ? (
            <>
              <label className="arena-season-picker">
                <span className="arena-season-picker-label">{t('seasonPick')}</span>
                <select
                  className="arena-season-select"
                  value={seasonKey || `${seasons.year}-${seasons.month}`}
                  onChange={(e) => setSeasonKey(e.target.value)}
                >
                  {(seasons.seasons || []).map((season) => (
                    <option key={`${season.year}-${season.month}`} value={`${season.year}-${season.month}`}>
                      {formatSeasonLabel(season.year, season.month, i18n.language)}
                    </option>
                  ))}
                </select>
              </label>
              <h2 className="arena-section-title">{t('yourFinish')}</h2>
              <div className="arena-summary">
                <div className="arena-stat" style={authStatPanelStyle}>
                  <span className="arena-stat-label">{t('tabOffline')}</span>
                  <span className="arena-stat-value">{placeText(seasons.offline?.me)}</span>
                </div>
                <div className="arena-stat" style={authStatPanelStyle}>
                  <span className="arena-stat-label">{t('tabOnline')}</span>
                  <span className="arena-stat-value">{placeText(seasons.online?.me)}</span>
                </div>
              </div>
              {!seasons.offline?.me?.position && !seasons.online?.me?.position && (
                <p className="arena-empty">{t('notInSeason')}</p>
              )}
              <h2 className="arena-section-title">{t('tabOffline')}</h2>
              {renderSeasonBoard(seasons.offline?.ranking, 'offline')}
              <h2 className="arena-section-title">{t('tabOnline')}</h2>
              {renderSeasonBoard(seasons.online?.ranking, 'online')}
            </>
          ) : (
            <p className="arena-empty">{t('noSeasons')}</p>
          )
        )}
      </AuthLayeredCard>
      {bonusOpen && (
        <div className="popup-overlay" role="dialog" aria-modal="true" aria-label={t('bonusTitle')}>
          <div className="popup-content popup-content-small auth-card" style={authCardStyle}>
            <div className="popup-header">
              <h3>{t('bonusTitle')}</h3>
            </div>
            <div className="popup-body">
              <p className="arena-help-block">{t('bonusBody')}</p>
              <p className="arena-help-block">{t('bonusBalance', { count: characterCtx?.character?.ruhParasi ?? 0 })}</p>
              {bonusError && <p className="arena-empty">{bonusError}</p>}
            </div>
            <div className="popup-footer" style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              <button
                type="button"
                className="auth-button popup-button"
                style={authButtonStyle}
                disabled={bonusBuying}
                onClick={buyBonus}
              >
                {t('bonusPay')}
              </button>
              <button
                type="button"
                className="auth-button popup-button"
                style={authButtonSmallStyle}
                onClick={() => setBonusOpen(false)}
              >
                {tc('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {helpOpen && (
        <div className="popup-overlay" role="dialog" aria-modal="true" aria-label={t('helpLabel')}>
          <div className="popup-content auth-card" style={authCardStyle}>
            <div className="popup-header">
              <h3>{t('title')}</h3>
            </div>
            <div className="popup-body arena-help-body">
              <section className="arena-help-block">
                <h3>{t('helpLegend')}</h3>
                <p>{t('helpLegendBody')}</p>
              </section>
              {['Offline', 'Online'].map((side) => (
                <section key={side} className="arena-help-block">
                  <h3>{t(side === 'Offline' ? 'helpOffline' : 'helpOnline')}</h3>
                  <h4>{t('helpTitles')}</h4>
                  <p>{t(side === 'Offline' ? 'helpOfflineTitles' : 'helpOnlineTitles')}</p>
                  <h4>{t('helpOpponents')}</h4>
                  <p>{t(side === 'Offline' ? 'helpOfflineOpponents' : 'helpOnlineOpponents')}</p>
                  <h4>{t('helpPoints')}</h4>
                  <p>{t(side === 'Offline' ? 'helpOfflinePoints' : 'helpOnlinePoints')}</p>
                  <h4>{t('helpRank')}</h4>
                  <p>{t(side === 'Offline' ? 'helpOfflineRank' : 'helpOnlineRank')}</p>
                </section>
              ))}
              <section className="arena-help-block">
                <h3>{t('helpSeasons')}</h3>
                <p>{t('helpSeasonsBody')}</p>
              </section>
              <section className="arena-help-block">
                <h3>{t('helpReplays')}</h3>
                <p>{t('helpReplayBody')}</p>
              </section>
            </div>
            <div className="popup-footer" style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                className="auth-button popup-button"
                style={authButtonStyle}
                onClick={() => setHelpOpen(false)}
              >
                {tc('ok')}
              </button>
            </div>
          </div>
        </div>
      )}
      {searching && (
        <div className="popup-overlay" role="dialog" aria-modal="true" aria-label={t('searchTitle')}>
          <div className="popup-content popup-content-small auth-card" style={authCardStyle}>
            <div className="popup-header">
              <h3>{t('searchTitle')}</h3>
            </div>
            <div className="popup-body">
              <p className="arena-search-sec">{t('searchSeconds', { sec: searchSec })}</p>
              {searchPhase === 'any' ? (
                <p className="arena-search-text">{t('searchAny')}</p>
              ) : searchPhase === 'wide' ? (
                <>
                  <p className="arena-search-text">{t('searchWide', { min: searchMin, max: searchMax })}</p>
                  <p className="arena-search-next">{t('searchWideNext')}</p>
                </>
              ) : (
                <>
                  <p className="arena-search-text">{t('searchClose', { rating: myRating, min: searchMin, max: searchMax })}</p>
                  <p className="arena-search-next">{t('searchCloseNext')}</p>
                </>
              )}
            </div>
            <div className="popup-footer" style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                className="auth-button popup-button"
                style={authButtonStyle}
                onClick={cancelQueue}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {fightStarting && (
        <div className="popup-overlay" role="alertdialog" aria-busy="true" aria-label={t('fightLoading')}>
          <div className="popup-content popup-content-small auth-card">
            <p className="arena-result-line">{t('fightLoading')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Arena;
